import { ChargeType, User } from '@core/generated/gql-types';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import { useClientStore } from '@core/client/useClientStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import {
  EMPTY_ASSIGNMENT_RATE_INPUT,
  type AssignmentRateInput,
} from './assignmentRateInputs';

interface AssignmentRateSectionProps {
  worker: User;
  clientId: string;
  classificationId: string;
  rateInput?: AssignmentRateInput;
  ordinaryWorkerPayRateCents?: number | null;
  ordinaryClientPayRateCents?: number | null;
  onChange: (updates: Partial<AssignmentRateInput>) => void;
}

const rateOptionClassName =
  'flex flex-1 cursor-pointer items-start gap-3 rounded-lg border border-gray-300 bg-white p-4';

const rateRadioClassName = 'mt-0.5 h-4 w-4 shrink-0';

const formatHourlyRate = (rateInCents: number | null | undefined) =>
  rateInCents == null
    ? 'Calculated on assignment'
    : `$${(rateInCents / 100).toFixed(2)}/hr`;

const AssignmentRateSection = ({
  worker,
  clientId,
  classificationId,
  rateInput = EMPTY_ASSIGNMENT_RATE_INPUT,
  ordinaryWorkerPayRateCents,
  ordinaryClientPayRateCents,
  onChange,
}: AssignmentRateSectionProps) => {
  const { getTerm } = useTemplateStore();
  const coreLoopConfigurationItems = useCoreLoopConfigurationStore(
    (state) => state.coreLoopConfigurationItems,
  );
  const getClientById = useClientStore((state) => state.getClientById);
  const classificationLevelClientRates = useClassificationStore(
    (state) => state.classificationLevelClientRates,
  );

  const classificationLevel = worker.classificationLevels?.find(
    (workerClassificationLevel) =>
      workerClassificationLevel?.classificationId === classificationId,
  );
  const workerMinimum = classificationLevel?.minimums?.[0];
  const workerPayRateCents =
    ordinaryWorkerPayRateCents ?? workerMinimum?.minimum;
  const client = getClientById(clientId);
  const clientClassificationRate = classificationLevelClientRates.find(
    (classificationRate) =>
      classificationRate.clientId === clientId &&
      classificationRate.classificationLevelId === classificationLevel?.id,
  );
  const matchingClientMinimum =
    clientClassificationRate?.minimums?.find(
      (minimum) => minimum.countryHash === workerMinimum?.countryHash,
    ) ?? clientClassificationRate?.minimums?.[0];

  let clientPayRateCents: number | null | undefined =
    ordinaryClientPayRateCents;
  if (clientPayRateCents == null && client?.chargeType === ChargeType.Fixed) {
    clientPayRateCents = matchingClientMinimum?.minimum;
  } else if (
    clientPayRateCents == null &&
    client?.chargeType === ChargeType.Margin &&
    workerPayRateCents != null
  ) {
    const marginPercentage =
      client.marginOverridePercentage ??
      coreLoopConfigurationItems?.marginPercentage ??
      0;
    clientPayRateCents =
      workerPayRateCents + Math.round(workerPayRateCents * marginPercentage);
  }

  const classificationLevelName = classificationLevel?.name;

  return (
    <div className="mt-6 rounded-lg border border-gray-200 bg-gray-50 p-5">
      <div className="mb-4">
        <h4 className="text-base font-semibold text-gray-900">
          Rate for this assignment
        </h4>
        <p className="mt-1 text-sm text-gray-500">
          {worker.firstName} {worker.lastName}
          {classificationLevelName ? ` — ${classificationLevelName}` : ''}
        </p>
      </div>

      <div className="grid gap-5 md:grid-cols-2">
        <fieldset className="flex h-full flex-col">
          <legend className="mb-2 text-sm font-medium text-gray-700">
            {getTerm(TemplateTerm.WORKER, false)} pay
          </legend>
          <div className="flex flex-1 flex-col gap-2">
            <label className={rateOptionClassName}>
              <input
                type="radio"
                className={rateRadioClassName}
                name={`worker-pay-${worker.id}`}
                checked={!rateInput.useCustomWorkerPayRate}
                onChange={() => onChange({ useCustomWorkerPayRate: false })}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  Pay {formatHourlyRate(workerPayRateCents)}
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  {classificationLevelName
                    ? `${classificationLevelName} assigned rate`
                    : 'Ordinary assigned rate'}
                </span>
              </span>
            </label>
            <label className={rateOptionClassName}>
              <input
                type="radio"
                className={rateRadioClassName}
                name={`worker-pay-${worker.id}`}
                checked={rateInput.useCustomWorkerPayRate}
                onChange={() => onChange({ useCustomWorkerPayRate: true })}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">
                  Set a different rate for this job
                </span>
                {rateInput.useCustomWorkerPayRate && (
                  <span className="relative mt-2 block">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={rateInput.workerPayRateDollars}
                      onChange={(event) =>
                        onChange({ workerPayRateDollars: event.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 py-2 pr-12 pl-7 text-sm"
                      placeholder="0.00"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">
                      /hr
                    </span>
                  </span>
                )}
              </span>
            </label>
          </div>
        </fieldset>

        <fieldset className="flex h-full flex-col">
          <legend className="mb-2 text-sm font-medium text-gray-700">
            Client charge
          </legend>
          <div className="flex flex-1 flex-col gap-2">
            <label className={rateOptionClassName}>
              <input
                type="radio"
                className={rateRadioClassName}
                name={`client-pay-${worker.id}`}
                checked={!rateInput.useCustomClientPayRate}
                onChange={() => onChange({ useCustomClientPayRate: false })}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">
                  Charge {formatHourlyRate(clientPayRateCents)}
                </span>
                <span className="mt-1 block text-xs text-gray-500">
                  Client&apos;s ordinary charge rate
                </span>
              </span>
            </label>
            <label className={rateOptionClassName}>
              <input
                type="radio"
                className={rateRadioClassName}
                name={`client-pay-${worker.id}`}
                checked={rateInput.useCustomClientPayRate}
                onChange={() => onChange({ useCustomClientPayRate: true })}
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-medium text-gray-900">
                  Set a different rate for this job
                </span>
                {rateInput.useCustomClientPayRate && (
                  <span className="relative mt-2 block">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={rateInput.clientPayRateDollars}
                      onChange={(event) =>
                        onChange({ clientPayRateDollars: event.target.value })
                      }
                      className="w-full rounded-md border border-gray-300 py-2 pr-12 pl-7 text-sm"
                      placeholder="0.00"
                    />
                    <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-gray-500">
                      /hr
                    </span>
                  </span>
                )}
              </span>
            </label>
          </div>
        </fieldset>
      </div>
    </div>
  );
};

export default AssignmentRateSection;
